import { readableCourseTitle } from '@/app/lib/courseTitle';
import type { NotificationData } from '@/app/types/interfaces';

export interface NotificationView {
  kind: 'seat-open' | 'general';
  title: string;
  details: string[];
  /** Catalog course id (e.g. 16198512) when the message names a course code. */
  courseId?: string;
  /** Rutgers WebReg link that registers the opened index. */
  registerUrl?: string;
  /** Unread seat openings stay urgent for a day; sections refill fast. */
  actNow: boolean;
}

const SEAT_OPEN = /^A spot in (?:section )?(.+?) is now open!\s*(?:Course:\s*(.+))?$/i;
const COURSE_NOTE = /^(.*?)\s+-\s+Section\s+(\S+)\s+\(([\d:]+),\s*Index\s+(\d+)\)$/i;
const ACT_NOW_MS = 24 * 60 * 60 * 1000;

/**
 * Notifications arrive as plain sentences from the Sniper pipeline. Turn the
 * seat-opening sentence into a readable headline with its section facts and a
 * WebReg action; everything else is shown as written.
 */
export function describeNotification(notification: NotificationData, now = Date.now()): NotificationView {
  const seat = notification.message.trim().match(SEAT_OPEN);
  if (!seat) return { kind: 'general', title: notification.message, details: [], actNow: false };
  const [, label, note] = seat;
  const course = note?.trim().match(COURSE_NOTE);
  const registerUrl = notification.url?.startsWith('https://sims.rutgers.edu/') ? notification.url : undefined;
  const actNow = !notification.is_read && now - new Date(notification.created_at).getTime() < ACT_NOW_MS;
  if (course) {
    const [, name, section, code, index] = course;
    return { kind: 'seat-open', title: readableCourseTitle(name.trim()), details: [`Section ${section}`, `Index ${index}`, code], courseId: code.replace(/:/g, ''), registerUrl, actNow };
  }
  return { kind: 'seat-open', title: note?.trim() ? readableCourseTitle(note.trim()) : `Section ${label}`, details: /^\d+$/.test(label) ? [`Index ${label}`] : [], registerUrl, actNow };
}

export function formatNotificationAge(dateString: string, now = Date.now()) {
  const minutes = Math.floor((now - new Date(dateString).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1_440) return `${Math.floor(minutes / 60)} h`;
  if (minutes < 10_080) return `${Math.floor(minutes / 1_440)} d`;
  return new Date(dateString).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
